// First Created - 22 Oct 2026
// Author - Atul Pandey

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'

import { apiSlice } from '@/app/api/apiSlice'


/*
|--------------------------------------------------------------------------
| 01 - Coach User Session Evaluation Matrix Adapter
|--------------------------------------------------------------------------
*/

const coachUserSessionEvalMatrixsAdapter =
    createEntityAdapter({})

const initialState =
    coachUserSessionEvalMatrixsAdapter.getInitialState()


/*
|--------------------------------------------------------------------------
| 02 - Coach User Session Evaluation Matrix API Slice
|--------------------------------------------------------------------------
*/

export const coachUserSessionEvalMatrixsApiSlice =
    apiSlice.injectEndpoints({

        endpoints: builder => ({

            /*
            |--------------------------------------------------------------------------
            | Get Coach User Session Evaluation Matrices
            |--------------------------------------------------------------------------
            |
            | Optional filters:
            |
            | coachId (EtAdmin only when querying another coach)
            | userId
            | orgId
            | trailId
            | sessionId
            | cohortId
            |
            | Cohort is a filter, not part of uniqueness.
            |
            */

            getCoachUserSessionEvalMatrixs: builder.query({

                query: ({
                    coachId,
                    userId,
                    orgId,
                    trailId,
                    sessionId,
                    cohortId,
                } = {}) => ({

                    url: '/coachUserSessionEvalMatrixs/getCoachUserSessionEvalMatrixs',

                    params: {

                        ...(coachId && { coachId }),

                        ...(userId && { userId }),

                        ...(orgId && { orgId }),

                        ...(trailId && { trailId }),

                        ...(sessionId && { sessionId }),

                        ...(cohortId && { cohortId }),

                    },

                    validateStatus: (response, result) => {
                        return response.status === 200 && !result.isError
                    },

                }),

                transformResponse: responseData => {

                    const loadedMatrices = responseData.map(
                        matrix => ({

                            ...matrix,

                            id: matrix._id,

                        })
                    )

                    return coachUserSessionEvalMatrixsAdapter.setAll(
                        initialState,
                        loadedMatrices
                    )

                },

                providesTags: (result) => {

                    if (result?.ids) {

                        return [
                            {
                                type: 'CoachUserSessionEvalMatrix',
                                id: 'LIST'
                            },

                            ...result.ids.map(
                                id => ({
                                    type: 'CoachUserSessionEvalMatrix',
                                    id
                                })
                            )
                        ]

                    }

                    return [
                        {
                            type: 'CoachUserSessionEvalMatrix',
                            id: 'LIST'
                        }
                    ]

                },

            }),


            /*
            |--------------------------------------------------------------------------
            | Get Coach User Session Evaluation Matrix By ID
            |--------------------------------------------------------------------------
            */

            getCoachUserSessionEvalMatrix: builder.query({

                query: ({ id }) => ({

                    url: '/coachUserSessionEvalMatrixs/getCoachUserSessionEvalMatrix',

                    params: {
                        id
                    },

                    validateStatus: (response, result) => {
                        return response.status === 200 && !result.isError
                    },

                }),

                providesTags: (result, error, arg) => [
                    {
                        type: 'CoachUserSessionEvalMatrix',
                        id: arg?.id
                    }
                ],

            }),


            /*
            |--------------------------------------------------------------------------
            | Create New Coach User Session Evaluation Matrix
            |--------------------------------------------------------------------------
            |
            | Required:
            |
            | coachOrgTrailSessionPadId
            | reflectionNoteId
            | reflectionNoteType
            |
            | Optional:
            |
            | sessionEvals[]
            |
            | Backend derives coach/user/session context.
            |
            */

            createNewCoachUserSessionEvalMatrix: builder.mutation({

                query: initialMatrix => ({

                    url: '/coachUserSessionEvalMatrixs/createNewCoachUserSessionEvalMatrix',

                    method: 'POST',

                    body: {
                        ...initialMatrix
                    },

                }),

                invalidatesTags: [
                    {
                        type: 'CoachUserSessionEvalMatrix',
                        id: 'LIST'
                    }
                ],

            }),


            /*
            |--------------------------------------------------------------------------
            | Update Coach User Session Evaluation Matrix
            |--------------------------------------------------------------------------
            |
            | Expected payload:
            |
            | id
            | sessionEvals[]
            | removeDimensionIds[]
            |
            | Partial updates are supported.
            |
            */

            updateCoachUserSessionEvalMatrix: builder.mutation({

                query: initialMatrix => ({

                    url: '/coachUserSessionEvalMatrixs/updateCoachUserSessionEvalMatrix',

                    method: 'PUT',

                    body: {
                        ...initialMatrix
                    },

                }),

                invalidatesTags: (result, error, arg) => [
                    {
                        type: 'CoachUserSessionEvalMatrix',
                        id: 'LIST'
                    },
                    {
                        type: 'CoachUserSessionEvalMatrix',
                        id: arg.id
                    }
                ],

            }),

        }),

    })


/*
|--------------------------------------------------------------------------
| 03 - Export Hooks
|--------------------------------------------------------------------------
*/

export const {

    useGetCoachUserSessionEvalMatrixsQuery,
    useLazyGetCoachUserSessionEvalMatrixsQuery,

    useGetCoachUserSessionEvalMatrixQuery,

    useCreateNewCoachUserSessionEvalMatrixMutation,
    useUpdateCoachUserSessionEvalMatrixMutation,

} = coachUserSessionEvalMatrixsApiSlice


/*
|--------------------------------------------------------------------------
| 04 - Matrix Selectors
|--------------------------------------------------------------------------
|
| Each distinct query argument has its own RTK Query cache.
|
|--------------------------------------------------------------------------
*/

export const selectCoachUserSessionEvalMatrixsResult =
    (arg = {}) =>
        coachUserSessionEvalMatrixsApiSlice
            .endpoints
            .getCoachUserSessionEvalMatrixs
            .select(arg)


export const makeSelectCoachUserSessionEvalMatrixsData =
    (arg = {}) =>
        createSelector(
            selectCoachUserSessionEvalMatrixsResult(arg),
            result => result.data
        )


export const makeCoachUserSessionEvalMatrixSelectors =
    (arg = {}) => {

        const selectData =
            makeSelectCoachUserSessionEvalMatrixsData(arg)

        return coachUserSessionEvalMatrixsAdapter.getSelectors(
            state => selectData(state) ?? initialState
        )

    }


/*
|--------------------------------------------------------------------------
| 05 - Default Matrix Selectors
|--------------------------------------------------------------------------
|
| These are for the unfiltered query cache.
|
| For a specific user/session/cohort, use
| makeCoachUserSessionEvalMatrixSelectors(queryArg).
|
|--------------------------------------------------------------------------
*/

export const {

    selectAll: selectAllCoachUserSessionEvalMatrixs,
    selectById: selectCoachUserSessionEvalMatrixById,
    selectIds: selectCoachUserSessionEvalMatrixIds,

} = makeCoachUserSessionEvalMatrixSelectors()